import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Folder,
  FileImage,
  FileText,
  FileSpreadsheet,
  FileVideo,
  File,
  ChevronRight,
  ArrowLeft,
  Search,
  Eye,
  Check,
  RefreshCw,
  LayoutGrid,
  List,
} from "lucide-react";
import { API, apiFetch, Modal, EmptyState } from "./_shared";

function normalizePrefix(p) {
  const s = String(p || "");
  return s;
}

function splitBreadcrumb(prefix) {
  const p = normalizePrefix(prefix);
  const parts = p.split("/").filter(Boolean);
  const crumbs = [{ label: "root", prefix: "" }];
  let acc = "";
  for (const part of parts) {
    acc += `${part}/`;
    crumbs.push({ label: part, prefix: acc });
  }
  return crumbs;
}

function folderLabel(folderPrefix) {
  const parts = String(folderPrefix || "").split("/").filter(Boolean);
  return parts[parts.length - 1] || folderPrefix;
}

function getFileExt(nameOrKey) {
  const s = String(nameOrKey || "");
  const base = s.split("?")[0].split("#")[0];
  const last = base.split("/").pop() || base;
  const i = last.lastIndexOf(".");
  if (i < 0) return "";
  return last.slice(i + 1).toLowerCase();
}

function getFileIconByExt(ext) {
  const e = String(ext || "").toLowerCase();
  if (["png", "jpg", "jpeg", "webp", "gif", "svg", "bmp", "heic"].includes(e)) return FileImage;
  if (["pdf"].includes(e)) return FileText;
  if (["xls", "xlsx", "csv", "ods"].includes(e)) return FileSpreadsheet;
  if (["mp4", "webm", "mov", "mkv", "avi", "m4v"].includes(e)) return FileVideo;
  if (["txt", "md", "json", "xml", "yml", "yaml", "log"].includes(e)) return FileText;
  return File;
}

function isImageExt(ext) {
  const e = String(ext || "").toLowerCase();
  return ["png", "jpg", "jpeg", "webp", "gif", "svg", "bmp", "heic"].includes(e);
}
function isVideoExt(ext) {
  const e = String(ext || "").toLowerCase();
  return ["mp4", "webm", "mov", "m4v", "mkv", "avi"].includes(e);
}

function canPreview(ext) {
  const e = String(ext || "").toLowerCase();
  if (isImageExt(e)) return true;
  if (e === "pdf") return true;
  if (isVideoExt(e)) return true;
  if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(e)) return true;
  return false;
}

const PreviewPane = ({ item }) => {
  if (!item?.url) {
    return (
      <div className="flex h-full items-center justify-center rounded-2xl border border-admin-border bg-admin-muted p-6 text-sm text-admin-textMuted">
        Chọn một file để xem trước
      </div>
    );
  }

  const ext = String(item.ext || "").toLowerCase();
  const url = item.url;

  if (isImageExt(ext)) {
    return (
      <div className="flex items-center justify-center overflow-hidden rounded-2xl border border-admin-border bg-admin-surface p-3 shadow-sm">
        <img
          src={url}
          alt={item.name || "preview"}
          className="block max-h-[70vh] w-auto max-w-full object-contain"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      </div>
    );
  }

  if (ext === "pdf") {
    return (
      <div className="overflow-hidden rounded-2xl border border-admin-border bg-admin-surface shadow-sm">
        <iframe title="PDF preview" src={url} className="h-[60vh] w-full" />
      </div>
    );
  }

  if (isVideoExt(ext)) {
    return (
      <div className="overflow-hidden rounded-2xl border border-admin-border bg-admin-surface shadow-sm">
        <video src={url} controls className="h-[60vh] w-full bg-black" />
      </div>
    );
  }

  if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext)) {
    const office = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    return (
      <div className="space-y-2">
        <div className="overflow-hidden rounded-2xl border border-admin-border bg-admin-surface shadow-sm">
          <iframe title="Office preview" src={office} className="h-[60vh] w-full" />
        </div>
        <div className="rounded-2xl border border-admin-border bg-admin-muted px-4 py-3 text-xs text-admin-textMuted">
          Preview Office phụ thuộc URL public và dịch vụ viewer của Microsoft.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-admin-border bg-admin-surface p-4 shadow-sm">
      <p className="text-sm font-extrabold text-admin-text">Không hỗ trợ preview</p>
      <p className="mt-1 text-xs text-admin-textMuted">Bạn vẫn có thể “Chọn” để dùng file này.</p>
    </div>
  );
};

/**
 * MediaPickerModal
 * - Browse S3 folders/files via GET /api/media?prefix=...
 * - Preview supported formats (image/pdf/video/office viewer)
 * - Select returns { key, url, name, ext, size, lastModified }
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - onSelect: (file) => void
 * - title?: string
 * - initialPrefix?: string
 * - accept?: "any" | "image" | "video"
 */
export default function MediaPickerModal({
  open,
  onClose,
  onSelect,
  title = "Chọn file từ thư viện",
  initialPrefix = "site/",
  accept = "any",
}) {
  const [prefix, setPrefix] = useState(initialPrefix);
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null); // selected file row
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [thumbnailMode, setThumbnailMode] = useState(true);

  const crumbs = useMemo(() => splitBreadcrumb(prefix), [prefix]);

  // Tìm kiếm chạy trên server (toàn thư viện), không chỉ trong danh sách đã tải.
  const load = useCallback(
    async (pfx, term = "") => {
      setLoading(true);
      try {
        const q = new URLSearchParams({ sort: "date", order: "desc", limit: "200" });
        if (pfx) q.set("prefix", pfx);
        if (term) q.set("q", term);
        if (accept === "image" || accept === "video") q.set("type", accept);
        const res = await apiFetch(`${API}/media/library?${q.toString()}`);
        setFolders((Array.isArray(res.folders) ? res.folders : []).map((f) => f.prefix));
        setItems(Array.isArray(res.items) ? res.items : []);
      } finally {
        setLoading(false);
      }
    },
    [accept]
  );

  useEffect(() => {
    if (!open) return;
    setPrefix(initialPrefix);
    setSearch("");
    setActive(null);
    setPreviewOpen(false);
    setPreviewItem(null);
    setThumbnailMode(true);
  }, [open, initialPrefix]);

  useEffect(() => {
    if (!open) return undefined;
    const term = search.trim();
    const t = setTimeout(() => load(prefix, term), term ? 300 : 0);
    return () => clearTimeout(t);
  }, [open, prefix, search, load]);

  const goUp = () => {
    const p = normalizePrefix(prefix);
    if (!p) return;
    const parts = p.split("/").filter(Boolean);
    if (parts.length <= 1) return setPrefix("");
    const up = parts.slice(0, -1).join("/") + "/";
    setPrefix(up);
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const folderRows = [...folders]
      .map((pfx) => ({ type: "folder", prefix: pfx, name: folderLabel(pfx) }))
      .filter((r) => !q || r.name.toLowerCase().includes(q));

    const fileRows = [...items]
      .map((it) => {
        const parts = String(it.key || "").split("/");
        const name = parts[parts.length - 1] || it.key;
        const ext = getFileExt(name);
        return { type: "file", ...it, name, ext };
      })
      .filter((r) => !q || String(r.name || "").toLowerCase().includes(q))
      .filter((r) => {
        if (accept === "image") return isImageExt(r.ext);
        if (accept === "video") return isVideoExt(r.ext);
        return true;
      });

    return [...folderRows, ...fileRows];
  }, [folders, items, search, accept]);

  const pick = (file) => {
    if (!file?.key || !file?.url) return;
    onSelect?.({
      key: file.key,
      url: file.url,
      name: file.name,
      ext: file.ext,
      size: file.size,
      lastModified: file.lastModified,
    });
    onClose?.();
  };

  const openPreview = (file) => {
    if (!file?.url) return;
    setPreviewItem(file);
    setPreviewOpen(true);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      maxWidthClass="max-w-7xl"
      footer={
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-admin-border bg-admin-surface py-2.5 text-sm font-extrabold text-admin-text shadow-sm"
          >
            Đóng
          </button>
          {active?.type === "file" && (
            <button
              type="button"
              onClick={() => pick(active)}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-admin-primary py-2.5 text-sm font-extrabold text-white shadow-soft-md transition hover:bg-admin-primaryHover"
            >
              <Check size={16} />
              Chọn file
            </button>
          )}
        </div>
      }
    >
      <style>{`
        .media-picker-modal .min-h-0 { min-height: 0; }
      `}</style>
      {previewOpen && (
        <Modal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          title={previewItem?.name ? `Xem: ${previewItem.name}` : "Xem trước"}
          maxWidthClass="max-w-5xl"
          footer={
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="flex-1 rounded-xl border border-admin-border bg-admin-surface py-2.5 text-sm font-extrabold text-admin-text shadow-sm"
              >
                Đóng
              </button>
              {previewItem?.type === "file" && (
                <button
                  type="button"
                  onClick={() => pick(previewItem)}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-admin-primary py-2.5 text-sm font-extrabold text-white shadow-soft-md transition hover:bg-admin-primaryHover"
                >
                  <Check size={16} />
                  Chọn file
                </button>
              )}
            </div>
          }
        >
          <PreviewPane item={previewItem} />
        </Modal>
      )}

      <div className="min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-2 text-sm text-admin-text">
              <span className="text-xs font-semibold text-admin-textMuted">Đường dẫn:</span>
              <div className="flex min-w-0 flex-wrap items-center gap-1">
                {crumbs.map((c, idx) => (
                  <React.Fragment key={c.prefix || "root"}>
                    {idx !== 0 && <ChevronRight size={14} className="text-admin-textMuted" />}
                    <button
                      type="button"
                      onClick={() => setPrefix(c.prefix)}
                      className={`rounded-lg px-2 py-1 text-xs font-bold transition ${
                        c.prefix === prefix
                          ? "bg-admin-primary text-white shadow-sm"
                          : "bg-admin-muted text-admin-text hover:bg-admin-muted/80"
                      }`}
                    >
                      {c.label}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="inline-flex items-center overflow-hidden rounded-xl border border-admin-border bg-admin-surface shadow-sm"
                title="Đổi chế độ hiển thị"
              >
                <button
                  type="button"
                  onClick={() => setThumbnailMode(true)}
                  className={`flex h-10 w-10 items-center justify-center transition ${
                    thumbnailMode
                      ? "bg-admin-muted text-admin-primary"
                      : "text-admin-textMuted hover:bg-admin-muted/60"
                  }`}
                  title="Thumbnail"
                >
                  <LayoutGrid size={18} />
                </button>
                <div className="h-6 w-px bg-admin-border" />
                <button
                  type="button"
                  onClick={() => setThumbnailMode(false)}
                  className={`flex h-10 w-10 items-center justify-center transition ${
                    !thumbnailMode
                      ? "bg-admin-muted text-admin-primary"
                      : "text-admin-textMuted hover:bg-admin-muted/60"
                  }`}
                  title="Danh sách"
                >
                  <List size={18} />
                </button>
              </div>
              <button
                type="button"
                onClick={goUp}
                disabled={!prefix}
                className="inline-flex items-center gap-2 rounded-xl border border-admin-border bg-admin-surface px-3 py-2 text-sm font-extrabold text-admin-text shadow-sm transition hover:bg-admin-muted disabled:opacity-50"
                title="Lên thư mục"
              >
                <ArrowLeft size={16} />
                Lên
              </button>
              <button
                type="button"
                onClick={() => load(prefix)}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-admin-border bg-admin-surface text-admin-textMuted shadow-sm transition hover:border-admin-primary/40 hover:text-admin-primary active:scale-95"
                title="Tải lại"
              >
                <RefreshCw size={15} />
              </button>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2 rounded-2xl border border-admin-border bg-admin-surface px-3 py-2 text-sm text-admin-textMuted shadow-sm">
            <Search size={16} className="text-admin-textMuted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm trong thư mục…"
              className="w-full bg-transparent text-sm outline-none"
            />
          </div>

          <div className="mt-4 overflow-hidden rounded-3xl border border-admin-border bg-admin-surface shadow-sm">
            {loading ? (
              <div className="p-6 text-sm text-admin-textMuted">Đang tải…</div>
            ) : filteredRows.length === 0 ? (
              <EmptyState icon={File} title="Không có nội dung" description="Không có file/folder phù hợp." />
            ) : (
              thumbnailMode ? (
                <div className="max-h-[65vh] overflow-y-auto p-4">
                  <div
                    className="grid gap-4"
                    style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}
                  >
                    {filteredRows.map((r) => {
                      const isActive = active && r.type === "file" && active.key === r.key;
                      const isFolder = r.type === "folder";
                      const ext = r.ext || getFileExt(r.name || "");
                      const isImg = !isFolder && isImageExt(ext);
                      const canPick = r.type === "file";
                      const canPrev = r.type === "file" && canPreview(ext);
                      const Icon = isFolder ? Folder : getFileIconByExt(ext);

                      return (
                        <div
                          key={isFolder ? r.prefix : r.key}
                          className={[
                            "group relative overflow-hidden rounded-3xl border bg-admin-surface shadow-sm transition",
                            "hover:shadow-soft-md",
                            isActive
                              ? "border-admin-primary/40 ring-2 ring-admin-primary/15"
                              : "border-admin-border hover:border-admin-primary/25",
                          ].join(" ")}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              if (isFolder) {
                                setPrefix(r.prefix);
                                setActive(null);
                                return;
                              }
                              setActive(r);
                            }}
                            className="block w-full text-left"
                            title={isFolder ? "Bấm để mở folder" : "Bấm để chọn file"}
                          >
                            <div className="relative aspect-square w-full bg-admin-muted/40">
                              {isFolder ? (
                                <div className="flex h-full items-center justify-center">
                                  <div className="flex h-14 w-14 items-center justify-center rounded-3xl bg-admin-muted text-admin-primary ring-1 ring-admin-border">
                                    <Folder size={26} />
                                  </div>
                                </div>
                              ) : isImg ? (
                                <img
                                  src={r.url}
                                  alt={r.name || ""}
                                  className="h-full w-full object-cover"
                                  loading="lazy"
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                <div className="flex h-full items-center justify-center">
                                  <div className="flex h-14 w-14 items-center justify-center rounded-3xl bg-admin-surface text-admin-text ring-1 ring-admin-border">
                                    <Icon size={24} />
                                  </div>
                                </div>
                              )}

                              {/* Click hint overlay */}
                              <div className="pointer-events-none absolute inset-x-0 bottom-0 flex min-h-[3.25rem] items-end bg-black/50 px-3 pb-3 opacity-0 transition group-hover:opacity-100">
                                <div className="inline-flex items-center gap-2 rounded-full bg-admin-surface/95 px-3 py-1 text-xs font-extrabold text-admin-text shadow-sm">
                                  {isFolder ? "Bấm để mở" : "Bấm để chọn"}
                                </div>
                              </div>
                            </div>
                            <div className="px-3.5 py-2.5">
                              <p className="truncate text-[13px] font-extrabold text-admin-text">{r.name}</p>
                              <p className="mt-0.5 truncate font-mono text-[11px] text-admin-textMuted">
                                {isFolder ? r.prefix : (ext || "file")}
                              </p>
                            </div>
                          </button>

                          <div className="absolute right-2 top-2 flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition">
                            {!isFolder && canPrev && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openPreview(r); }}
                                className="flex h-9 w-9 items-center justify-center rounded-2xl border border-admin-border bg-admin-surface/95 text-admin-text shadow-sm transition hover:bg-admin-muted"
                                title="Xem"
                              >
                                <Eye size={16} />
                              </button>
                            )}
                            {canPick && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); pick(r); }}
                                className="flex h-9 w-9 items-center justify-center rounded-2xl bg-admin-primary text-white shadow-sm transition hover:bg-admin-primaryHover"
                                title="Chọn"
                              >
                                <Check size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="max-h-[65vh] overflow-y-auto">
                  <div className="grid grid-cols-[1fr,160px] gap-3 border-b border-admin-border bg-admin-muted/50 px-4 py-3 text-xs font-extrabold uppercase tracking-wider text-admin-textMuted">
                    <div>Tên</div>
                    <div className="text-right">Thao tác</div>
                  </div>
                  <div className="divide-y divide-admin-border">
                    {filteredRows.map((r) => {
                      const isActive = active && r.type === "file" && active.key === r.key;
                      const isFolder = r.type === "folder";
                      const ext = r.ext || getFileExt(r.name || "");
                      const Icon = isFolder ? Folder : getFileIconByExt(ext);
                      const canPick = r.type === "file";
                      const canPrev = r.type === "file" && canPreview(ext);
                      return (
                        <div
                          key={isFolder ? r.prefix : r.key}
                          className={`grid grid-cols-[1fr,160px] items-center gap-3 px-4 py-3 hover:bg-admin-muted/40 ${isActive ? "bg-admin-primary/10" : ""}`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              if (isFolder) { setPrefix(r.prefix); setActive(null); return; }
                              setActive(r);
                            }}
                            className="flex min-w-0 items-center gap-2 text-left"
                            title={isFolder ? "Mở folder" : "Chọn file"}
                          >
                            <Icon size={18} className={isFolder ? "text-admin-primary" : "text-admin-textMuted"} />
                            <span className="truncate text-sm font-semibold text-admin-text">{r.name}</span>
                          </button>
                          <div className="flex items-center justify-end gap-2">
                            {!isFolder && canPrev && (
                              <button
                                type="button"
                                onClick={() => openPreview(r)}
                                className="flex h-9 w-9 items-center justify-center rounded-xl border border-admin-border bg-admin-surface text-admin-textMuted shadow-sm transition hover:bg-admin-muted hover:text-admin-text"
                                title="Xem"
                              >
                                <Eye size={16} />
                              </button>
                            )}
                            {canPick && (
                              <button
                                type="button"
                                onClick={() => pick(r)}
                                className="inline-flex items-center gap-2 rounded-xl bg-admin-primary px-3 py-2 text-xs font-extrabold text-white shadow-sm transition hover:bg-admin-primaryHover"
                                title="Chọn file này"
                              >
                                <Check size={14} />
                                Chọn
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )
            )}
          </div>
          {active?.type === "file" && accept !== "any" && (
            <div className="mt-4 rounded-2xl border border-admin-border bg-admin-muted px-4 py-3 text-xs text-admin-textMuted">
              Đang lọc theo loại: <span className="font-bold">{accept}</span>
            </div>
          )}
      </div>
    </Modal>
  );
}

